import { useContext, useEffect, useRef, useState } from "react";
import { FaFileCircleCheck } from "react-icons/fa6";
import { FiShoppingBag } from "react-icons/fi";
import { MdCancel } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import Cabecalho from "../../components/Cabecalho";
import ChamadoCardChat, { ConteudoDaAcao } from "../../components/ChamadoCardChat";
import Menu from "../../components/Menu";
import MessageChat from "../../components/MessageChat";
import CmxInput from "../../components/ui/Input";
import { AuthContext } from "../../context/auth";
import {
  AcaoDe,
  DescEnum_TipoServicoChamado,
  SituacaoChamado,
  convertToArray,
  situacaoEhTerminal,
} from "../../enums/enums";
import { api } from "../../services/apiClient";
import { descricaoDaSituacao, useSituacoes } from "../../services/situacoes";
import { handleError } from "../../services/erros/ErrorHandler";

import "./style.css";

export default function SolicitacaoDet() {
  const navigate = useNavigate();
  const { chamadoid } = useParams();
  const [loading, setLoading] = useState(false);
  const [chamado, setChamado] = useState(null);
  const { user } = useContext(AuthContext);

  const divChatRef = useRef(null);

  const situacoes = useSituacoes();

  // Linha do tempo: os passos são as situações NÃO terminais, na ordem do cadastro, mais um passo
  // final de fechamento. O passo final não tem nome fixo — ele mostra como o chamado terminou
  // (Encerrado, Cancelado, ou o Baixado legado do Lizard) e, enquanto o chamado está aberto, mostra
  // o destino natural: "Encerrado". Nada aqui depende de os ids serem 1..N contíguos, então remover
  // uma situação do cadastro não desalinha a régua.
  const passosAndamento = situacoes.filter((s) => !situacaoEhTerminal(s.id));
  const rotuloPassoFinal = situacaoEhTerminal(chamado?.situacao)
    ? descricaoDaSituacao(situacoes, chamado?.situacao)
    : descricaoDaSituacao(situacoes, SituacaoChamado.Encerrado);
  const passos = [...passosAndamento.map((s) => s.descricao), rotuloPassoFinal];
  // Posição do chamado na régua: terminal = último passo; caso contrário, onde o id dele estiver.
  const posicaoAtual = situacaoEhTerminal(chamado?.situacao)
    ? passos.length - 1
    : passosAndamento.findIndex((s) => s.id === chamado?.situacao);

  const clientesList =
    Array.isArray(user?.clientes) && user.clientes.length > 0
      ? user.clientes
      : user && user.cliente_id
        ? [{ id: user.cliente_id, label: user.cliente || "" }]
        : [];

  useEffect(() => {
    carregarChamado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function carregarChamado() {
    setLoading(true);

    if (chamadoid) {
      const response = await api.get("/chamado/get", {
        params: {
          id: chamadoid,
        },
      });

      if (
        response.data &&
        clientesList.some((c) => c.id === response.data.contato_ti.cliente_id)
      ) {
        setChamado(response.data);
      } else {
        toast.warning(`Chamado ${chamadoid} não encontrado!`);
        navigate("/app/chamados");
      }
    }

    setLoading(false);
  }

  useEffect(() => {
    // Rolar para o final após a renderização dos itens
    if (divChatRef.current) {
      const div = divChatRef.current;
      div.scrollTop = div.scrollHeight;
    }
  }, [loading, chamado?.acoes]);

  async function handleCancelar() {
    try {
      const { value: motivo } = await Swal.fire({
        title: `Informar um motivo para o cancelamento do Chamado ${chamado?.id}`,
        input: "text",
        inputLabel: "Motivo Cancelamento",
        showCancelButton: true,
        confirmButtonText: "Continuar",
        cancelButtonText: "Cancelar",
        showLoaderOnConfirm: true,
      });

      if (!motivo) {
        return;
      }

      const data = {
        chamado_id: chamado?.id,
        motivo_cancelamento: motivo,
      };

      await api.put("/chamado/cancel", data);

      setChamado((prevChamado) => ({
        ...prevChamado,
        situacao: SituacaoChamado.Cancelado,
      }));

      toast.success("Cancelado com sucesso!");
    } catch (error) {
      handleError(error, "Erro ao Cancelar");
    }
  }

  async function handleAprovar() {
    try {
      const { value: motivo } = await Swal.fire({
        title: `Informar observações para aprovação do Chamado '${chamado?.id}'`,
        input: "textarea",
        inputLabel: "Observações",
        showCancelButton: true,
        confirmButtonText: "Continuar",
        cancelButtonText: "Cancelar",
        showLoaderOnConfirm: true,
      });

      if (!motivo) {
        return;
      }

      const data = {
        chamado_id: chamado?.id,
        motivo_aprovacao: motivo,
      };

      await api.put("/chamado/aprovar", data);

      setChamado((prevChamado) => ({
        ...prevChamado,
        aprovado_pelo_portal: true,
      }));

      carregarChamado();

      toast.success("Orçamento Aprovado com sucesso!");
    } catch (error) {
      handleError(error, "Erro ao Aprovar");
    }
  }

  // Regra CHA-039 do Raptor: texto e arquivos numa ação só, com o print onde o cursor estava. A caixa
  // (MessageChat) guarda os arquivos; aqui só se monta o envio e se repassa o arquivo arrastado para o chat.
  const [enviando, setEnviando] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const messageChatRef = useRef(null);

  async function handleSend(texto, arquivos) {
    if (enviando || (!texto && !arquivos.length)) return false;

    const data = new FormData();
    if (texto) data.append("descricao", texto);
    data.append("acao_de", AcaoDe.Cliente);
    data.append("criado_por", user.nome);
    data.append("chamado_id", chamado?.id);
    data.append(
      "cliente",
      (clientesList.find((c) => c.id === chamado?.contato_ti?.cliente_id) || {})
        .label || user.cliente,
    );
    arquivos.forEach((file) => data.append("file", file));

    setEnviando(true);
    try {
      const response = await api.post("/chamadoacao/cad", data);
      setChamado((anterior) => ({
        ...anterior,
        atualizado_em: response.data.criado_em,
        acoes: [...(anterior?.acoes || []), response.data],
      }));
      toast.success("Ação salva com sucesso!");
      return true;
    } catch (error) {
      handleError(error, "Erro ao salvar Ação");
      return false;
    } finally {
      setEnviando(false);
    }
  }

  const temArquivoNoArraste = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  function aoArrastarSobre(e) {
    if (!temArquivoNoArraste(e)) return;
    e.preventDefault();
    if (!arrastando) setArrastando(true);
  }
  function aoSairDoArraste(e) {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setArrastando(false);
  }
  // Solto em cima do texto: entra naquele ponto; solto em outro lugar do chat: no cursor (ou no fim).
  function aoSoltar(e) {
    if (!temArquivoNoArraste(e)) return;
    e.preventDefault();
    setArrastando(false);
    const range = document.caretRangeFromPoint ? document.caretRangeFromPoint(e.clientX, e.clientY) : null;
    const dentro = range && document.getElementById("acaoInput")?.contains(range.startContainer);
    messageChatRef.current?.adicionarArquivos(e.dataTransfer.files, dentro ? range : null);
  }

  return (
    <div>
      <Menu />
      <div className="content">
        <Cabecalho descricao="Chamado">
          <FiShoppingBag size={24} />
        </Cabecalho>

        <div className="content_area_detail">
          <div className="left_side_datail">
            <div className="left_side_datail_top">
              <h4>{`Chamado: ${chamado?.chamado_id}`}</h4>
            </div>
            <div className="timeline">
              {passos.map((passo, index) => (
                <div key={index} className={`timeline-step`}>
                  <div className="timeline-description">
                    {String(passo || "").replace(/ /g, "\n")}
                  </div>

                  <div
                    className={`timeline-circle ${
                      index === posicaoAtual
                        ? "active"
                        : index < posicaoAtual
                        ? "actived"
                        : ""
                    }`}
                  >
                    {index > 0 && (
                      <div
                        className={`timeline-line-before ${
                          index <= posicaoAtual ? "active" : ""
                        }`}
                      ></div>
                    )}
                    {index < passos.length - 1 && (
                      <div
                        className={`timeline-line-after ${
                          index < posicaoAtual ? "active" : ""
                        }`}
                      ></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="left_side_detail_bottom">
              <div className="row">
                <div className="col col-12">
                  <label>Assunto</label>
                  <CmxInput
                    type="text"
                    value={chamado?.assunto}
                    disabled={true}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col col-6">
                  <label>Chamado Cliente</label>
                  <CmxInput
                    type="text"
                    value={chamado?.chamado_cliente}
                    disabled={true}
                  />
                </div>
                <div className="col col-6">
                  <label>Rotina</label>
                  <CmxInput
                    type="text"
                    value={chamado?.rotina}
                    disabled={true}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col col-12">
                  <label>E-mail Solicitante</label>
                  <CmxInput
                    type="text"
                    value={chamado?.solicitante_email}
                    disabled={true}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col col-6">
                  <label>Serviço/Produto</label>
                  <CmxInput
                    type="text"
                    value={chamado?.servico_produto?.descricao || ""}
                    disabled={true}
                  />
                </div>
                <div className="col col-6">
                  <label>Contato TI</label>
                  <CmxInput
                    type="text"
                    value={chamado?.contato_ti.nome}
                    disabled={true}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col  col-6">
                  <label>Tipo</label>
                  <CmxInput
                    type="text"
                    value={DescEnum_TipoServicoChamado[chamado?.tipo]}
                    disabled={true}
                  />
                </div>
                <div className="col  col-6">
                  <label>Prioridade</label>
                  <CmxInput
                    type="text"
                    value={
                      chamado?.prioridade
                        ? chamado.prioridade.descricao
                        : chamado?.prioridade_id
                    }
                    disabled={true}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col col-12">
                  <label>Descrição</label>
                  {/* Regra CHA-039 do Raptor: a Descrição é a ação de abertura, com o print onde foi colado. */}
                  <div className="descricao_chamado">
                    <ConteudoDaAcao descricao={chamado?.descricao} anexos={chamado?.descricao_anexos} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`right_side_datail${arrastando ? " arrastando" : ""}`}
            onDragOver={aoArrastarSobre}
            onDragLeave={aoSairDoArraste}
            onDrop={aoSoltar}
          >
            <div className="right_side_datail_top">
              <h4>{`Ações do Chamado`}</h4>
              {/* <div className="header_icons"> */}
              <span>
                {chamado?.situacao === SituacaoChamado.Em_Aberto && (
                  <button
                    className="icon-button"
                    onClick={() => handleCancelar()}
                  >
                    <MdCancel />
                    <span>Cancelar Chamado</span>
                  </button>
                )}
                {chamado?.situacao === SituacaoChamado.Aguardando_Aprovacao &&
                  !chamado?.aprovado_pelo_portal &&
                  !chamado?.integracao_colcci && (
                    <button
                      className="icon-button-aprovar"
                      onClick={() => handleAprovar()}
                    >
                      <FaFileCircleCheck />
                      <span>Aprovar Orçamento</span>
                    </button>
                  )}
              </span>
              {/* </div> */}
            </div>

            <div className="right_side_datail_center">
              <div className="chat_box_datail" ref={divChatRef}>
                {loading ? (
                  <div>Carregando...</div>
                ) : (
                  <>
                    {chamado?.acoes.map((acao) => (
                      <ChamadoCardChat key={acao.id} acao={acao} />
                    ))}
                  </>
                )}
              </div>
            </div>

            <div className="right_side_detail_bottom">
              <MessageChat ref={messageChatRef} chamado={chamado} onSend={handleSend} enviando={enviando} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
